// lib/list/listOutputMentions.ts
import type { KernelHandle } from '#types/types';
import { isInternalAssetUrl } from '#lib/azure-shared';
import { readListProjectOutputs } from './listProjectOutputs';
import { SNAPSHOT_VERSION_NAMES, resolveVersionVideoLayer, resolveVideoLayer } from './listOutputMedia';
import { buildAssetRenditionUrl } from '#lib/asset-rendition';
import { normalizeProjectListTimestamp } from '#lib/project-list-projection';
import { getProjectStudioViewMode } from '#lib/studio-project-view';
import { getListAssetFileKey } from './listOutputGroups';

const outputToken = /(?<tildes>~{3,})[\s\S]*?(?:\k<tildes>|$)|(?<ticks>`+)[\s\S]*?(?:\k<ticks>|$)|!?\[(?:[^[\]\n]|\[[^\]\n]*\])*\](?:\([^)\n]*\)|\[[^\]\n]*\])|https?:\/\/[^\s]+|(?<prefix>^|[\s(])@(?:output:(?<id>[a-z0-9-]+)(?:\[(?<edit>last:)?(?<number>[1-9]\d*)\])?|(?<last>last))(?=$|[\s),.;:!?])/gi;

export function listOutputMentionOptions(kernel: KernelHandle, projectId: string, captureNumbers = false) {
  if (!projectId) return [];
  const { projection, layersByWorkspace } = readListProjectOutputs(kernel, projectId);
  const outputs = projection.flatMap(entry => {
    const item = entry.source === 'project-layer' ? entry.layer : entry.version;
    const url = entry.source === 'project-layer' ? entry.layer.imageSrc : entry.version.imageUrl;
    if (entry.source === 'workspace-version' && SNAPSHOT_VERSION_NAMES.has(entry.version.name)) return [];
    const snapshotWorkspace = /^ws-version-(.+)-\d+\.[a-z0-9]+$/i.exec(getListAssetFileKey(url).split('/').pop() ?? '')?.[1];
    const video = entry.source === 'project-layer'
      ? entry.layer.type === 'video' || entry.layer.videoSrc || entry.layer.videoFramesUrls?.length
      : (snapshotWorkspace === entry.workspaceId
        ? resolveVideoLayer(entry.version.layerVisibility, layersByWorkspace.get(entry.workspaceId) ?? [])
        : resolveVersionVideoLayer(entry.version, layersByWorkspace.get(entry.workspaceId) ?? []));
    if (video || !isInternalAssetUrl(url) || !/^[a-z0-9-]+$/i.test(item.id)) return [];
    return [{ id: item.id, handle: `output:${item.id}`, name: '', timestamp: Math.max(0, normalizeProjectListTimestamp(item.timestamp)), thumbnailUrl: buildAssetRenditionUrl(url, 64) ?? url, url, category: 'Outputs' as const }];
  });
  const userId = kernel.getState?.().userContext?.dbUserId ?? '';
  const stored = kernel.readEntityRuntimeState?.('Project', projectId)?.listOutputMentionIds;
  const ids = stored?.userId === userId ? [...stored.ids] : [];
  for (const output of outputs) if (!ids.includes(output.id)) ids.push(output.id);
  if (captureNumbers && userId && (stored?.userId !== userId || stored.ids.length !== ids.length)) {
    if (stored && stored.userId !== userId) kernel.setState({ chatOutputMentionScope: undefined });
    kernel.patchEntityRuntimeState('Project', projectId, { listOutputMentionIds: { userId, ids } });
  }
  return outputs.map(output => ({ ...output, name: `Output ${ids.indexOf(output.id) + 1}` }));
}

export function listOutputMentionIds(instruction: string) {
  return [...new Set([...instruction.matchAll(outputToken)].flatMap(match => match.groups?.id ? [match.groups.id] : []))];
}

/** Normalize the human brief only when its single output is the verified edit source. */
export function listSingleSourceEditBrief(brief: unknown, sourceId: string): string | undefined {
  if (typeof brief !== 'string' || !brief.trim()) return undefined;
  const ids = listOutputMentionIds(brief);
  if (ids.length !== 1 || ids[0] !== sourceId) return undefined;
  return brief.replace(outputToken, (...args) => {
    const groups = args[args.length - 1] as { id?: string; prefix?: string };
    return groups.id === sourceId ? `${groups.prefix ?? ''}the supplied edit source` : args[0];
  }).trim();
}

export function captureListOutputMention(kernel: KernelHandle, projectId: string, outputId: string, draft: string) {
  const state = kernel.getState();
  const userId = state.userContext?.dbUserId;
  const owner = state.chatOutputMentionScope;
  const hasAliases = [...draft.matchAll(outputToken)].some(match => match.groups?.id && /^-?\d+$/.test(match.groups.id) && !match.groups.number);
  if (!userId || (hasAliases && (owner?.projectId !== projectId || owner?.userId !== userId))) {
    throw new Error('Remove the output mentions from the previous project or account, then select them again.');
  }
  const output = listOutputMentionOptions(kernel, projectId, true).find(item => item.id === outputId);
  if (!output) throw new Error('That output is unavailable. Pick an output from the @ menu again.');
  kernel.setState({ chatOutputMentionScope: { projectId, userId } });
  return `Output:${output.name.slice('Output '.length)}`;
}

export function serializeListOutputMentions(kernel: KernelHandle, projectId: string, text: string) {
  let availableIds: Set<string> | undefined;
  // Validate existing numbered aliases before @last captures any new numbering.
  const numbered = text.replace(outputToken, (...args) => {
    const match = args[0] as string;
    const groups = args[args.length - 1] as { id?: string; number?: string; prefix?: string };
    if (!groups.id || groups.number || !/^-?\d+$/.test(groups.id)) return match;
    const state = kernel.getState();
    const userId = state.userContext?.dbUserId;
    const owner = state.chatOutputMentionScope;
    const stored = kernel.readEntityRuntimeState('Project', projectId)?.listOutputMentionIds;
    const number = Number(groups.id);
    const id = stored?.ids[number - 1];
    if (owner?.projectId !== projectId || owner?.userId !== userId || stored?.userId !== userId || !/^[1-9]\d*$/.test(groups.id)
      || !id || !(availableIds ??= new Set(listOutputMentionOptions(kernel, projectId).map(output => output.id))).has(id)) {
      throw new Error(`Output ${groups.id} is unavailable in this draft. Pick it from the @ menu again.`);
    }
    return `${groups.prefix ?? ''}@output:${id}[${number}]`;
  });
  const matches = [...numbered.matchAll(outputToken)];
  const lastIds = new Set(matches.flatMap(match => match.groups?.edit && match.groups.id ? [match.groups.id] : []));
  let latest: ReturnType<typeof listOutputMentionOptions>[number] | undefined;
  if (matches.some(match => match.groups?.last)) {
    if (getProjectStudioViewMode(kernel, projectId) !== 'marketing') return numbered;
    if (!kernel.getState().userContext?.dbUserId) throw new Error('Sign in before selecting @last.');
    latest = listOutputMentionOptions(kernel, projectId).sort((a, b) => b.timestamp - a.timestamp || a.id.localeCompare(b.id))[0];
    if (!latest) throw new Error('There is no image output to edit yet. Wait for a result before using @last.');
    lastIds.add(latest.id);
  }
  if (lastIds.size > 1) throw new Error('Choose only one @last edit target. Remove the conflicting output mention.');
  if (!latest) return numbered;
  const bound = listOutputMentionOptions(kernel, projectId, true).find(output => output.id === latest.id)!;
  return numbered.replace(outputToken, (...args) => {
    const groups = args[args.length - 1] as { last?: string; prefix?: string };
    return groups.last ? `${groups.prefix ?? ''}@output:${bound.id}[last:${bound.name.slice('Output '.length)}]` : args[0];
  });
}

export function resolveListOutputMentions(instruction: string, outputs: ReturnType<typeof listOutputMentionOptions>, inputUrls?: string[]) {
  const selected: typeof outputs = [];
  const editSources = new Set<string>();
  const prompt = instruction.replace(outputToken, (...args) => {
    const token = args[0] as string;
    const { id, edit, last, prefix = '' } = args[args.length - 1] as { id?: string; edit?: string; last?: string; prefix?: string };
    if (last) throw new Error('Select @last in the List composer and send it again.');
    if (!id) return token;
    const output = outputs.find(item => item.id === id);
    if (!output) throw new Error('That output is unavailable in this List project. Pick an output from the @ menu again.');
    if (!selected.some(item => item.id === id)) selected.push(output);
    if (edit) editSources.add(id);
    return `${prefix}reference image ${inputUrls ? inputUrls.indexOf(output.url) + 1 : selected.findIndex(item => item.id === id) + 1}`;
  });
  if (editSources.size > 1) throw new Error('Choose only one @last edit target.');
  const firstMention = [...instruction.matchAll(outputToken)].find(match => match.groups?.id);
  const editSourceUrl = selected.find(output => editSources.has(output.id))?.url ?? (
    selected.length === 1 && firstMention && /^(?:edit|modify|change|retouch)$/i.test(instruction.slice(0, firstMention.index).trim())
      ? selected[0]!.url : undefined);
  return { prompt, outputs: selected, editSourceUrl };
}

export async function hydrateListOutputMentions(kernel: KernelHandle, projectId: string, instruction: string, signal?: AbortSignal) {
  const ids = listOutputMentionIds(instruction);
  if (!ids.length) return;
  if (ids.length > 32) throw new Error('Select no more than 32 output references in one message.');
  const userId = kernel.getState().userContext?.dbUserId;
  const missing = () => {
    const available = new Set(listOutputMentionOptions(kernel, projectId).map(output => output.id));
    return ids.filter(id => !available.has(id));
  };
  for (const entity of ['StudioLayer', 'StudioWorkspaceVersion'] as const) {
    signal?.throwIfAborted();
    const needed = missing();
    if (!needed.length) break;
    await kernel.ensureInventoryIds(entity, needed);
    if (kernel.getState().userContext?.dbUserId !== userId) throw new Error('The account changed while resolving output references. Send the request again.');
  }
  signal?.throwIfAborted();
}

/** Durable examples supply references, but only current-turn selections may become an edit source. */
export function bindListGenerationReferences(
  command: { action_type?: unknown; parameters?: unknown },
  currentTurnUrls: string[],
  mentionedOutputs: ReturnType<typeof listOutputMentionOptions> = [],
  currentUserEditSource?: string,
  examples: ReturnType<typeof listOutputMentionOptions> = [],
) {
  if (command.action_type !== 'GENERATE_MEDIA' && command.action_type !== 'GENERATE_VARIATIONS') return command;
  const allowed = [...new Set(currentTurnUrls.filter(isInternalAssetUrl))];
  const referenceOutputs = [...new Map([...mentionedOutputs, ...examples].map(output => [output.id, output])).values()];
  const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const rawParameters = record(command.parameters);
  const validSource = (url: unknown) => typeof url === 'string' && allowed.includes(url.trim()) ? url.trim() : undefined;
  const explicitSource = validSource(currentUserEditSource);
  const rootSource = explicitSource ?? validSource(rawParameters.sourceImageUrl) ?? validSource(record(rawParameters.parameterOverrides).sourceImageUrl)
    ?? validSource(record(rawParameters.generationOptions).mainImageUrlOverride);
  const clean = (raw: unknown, inheritedSource = rootSource) => {
    const branch = { ...record(raw) };
    const source = explicitSource ?? validSource(branch.sourceImageUrl) ?? inheritedSource;
    if (source) branch.sourceImageUrl = source;
    else delete branch.sourceImageUrl;
    const outputSource = mentionedOutputs.find(output => output.url === source);
    if (outputSource) branch.listEditSourceOutputId = outputSource.id;
    else delete branch.listEditSourceOutputId;
    branch.referenceImageUrls = [...new Set([
      ...referenceOutputs.map(output => output.url),
      ...(Array.isArray(branch.referenceImageUrls) ? branch.referenceImageUrls.filter(url => allowed.includes(url)) : []),
    ])];
    const inputUrls = [...new Set([source, ...branch.referenceImageUrls as string[]].filter((url): url is string => typeof url === 'string'))];
    if (typeof branch.prompt === 'string') branch.prompt = resolveListOutputMentions(branch.prompt, referenceOutputs, inputUrls).prompt;
    return branch;
  };
  const parameters = clean(command.parameters);
  if (parameters.parameterOverrides) parameters.parameterOverrides = clean({ ...record(parameters.parameterOverrides), sourceImageUrl: rootSource });
  if (parameters.generationOptions) {
    const options = { ...record(parameters.generationOptions) };
    delete options.mainImagePrivateUrlOverride;
    if (rootSource) options.mainImageUrlOverride = rootSource;
    else delete options.mainImageUrlOverride;
    parameters.generationOptions = options;
  }
  if (Array.isArray(parameters.overrides)) parameters.overrides = parameters.overrides.map(branch => clean(branch));
  return { ...command, parameters: { ...parameters, __listCurrentSourceImageUrls: allowed } };
}
